import { defineStore } from 'pinia'
import { instance } from '@/services/api/instance'
import { disconnectFromKitchenHub } from '../services/api/orderService'
import { paymentRequestService } from '../services/paymentRequestService';
import { waiterCallService } from '../services/waiterCallService';

export const useOrderStore = defineStore('orders', {
  state: () => ({
    orders: [],
    currentOrder: null,
    kitchenOrders: [],
    kitchenConnectionStatus: 'disconnected',
    ungeseheneBestellungenCount: 0,
    newOrderSoundTrigger: 0,
    waiterNotifications: {
      paymentRequests: 0,
      hasNewPaymentRequest: false,
      waiterCalls: 0,
      hasNewWaiterCall: false
    },
    paymentRequests: [],
    waiterCalls: [],
    loading: false,
    error: null
  }),
  
  getters: {
    getOrders: (state) => state.orders,
    getCurrentOrder: (state) => state.currentOrder,
    getKitchenOrders: (state) => state.kitchenOrders,
    getKitchenConnectionStatus: (state) => state.kitchenConnectionStatus,
    getUngeseheneBestellungenCount: (state) => state.ungeseheneBestellungenCount,
    // Count of orders with status "Received" (new unprocessed orders)
    getReceivedOrdersCount: (state) => state.kitchenOrders.filter(order => order.status === 'Received').length,
    isLoading: (state) => state.loading,
    activeOrders: (state) => state.orders.filter(order => !['Completed', 'Cancelled'].includes(order.status)),
    ordersWantingToPay: (state) => state.activeOrders.filter(order => order.wantsToPay),
    ordersReadyForPickup: (state) => state.activeOrders.filter(order => order.status === 'Ready'),
    ordersBeingPrepared: (state) => state.activeOrders.filter(order => order.status === 'Preparing'),
    waiterNotificationCount: (state) => state.waiterNotifications.paymentRequests + state.waiterNotifications.waiterCalls,
    hasNewPaymentRequest: (state) => state.waiterNotifications.hasNewPaymentRequest,
    paymentRequestsList: (state) => state.paymentRequests,
    waiterCallsList: (state) => state.waiterCalls,
    hasNewWaiterCall: (state) => state.waiterNotifications.hasNewWaiterCall,
    getOrdersBySessionId: (state) => (sessionId) => {
      return state.orders.filter(order => order.sessionId === sessionId);
    },
  },
  
  actions: {
    setKitchenConnectionStatus(status) {
      if (['disconnected', 'connecting', 'connected', 'reconnecting', 'polling'].includes(status)) {
        this.kitchenConnectionStatus = status
      } else {
        console.error(`Ungültiger kitchenConnectionStatus: ${status}`)
      }
    },
    setKitchenOrders(newOrders, isInitialLoad = false) {
      // Wenn es das initiale Laden ist, ersetze einfach die Bestellungen ohne Hervorhebung
      if (isInitialLoad) {
        console.log("Initiales Laden der Küchen-Bestellungen");
        this.kitchenOrders = newOrders;
        this.loading = false;
        this.error = null;
        return;
      }

      const oldOrderIds = new Set(this.kitchenOrders.map(o => o.id))
      const trulyNewOrders = newOrders.filter(order => 
        !oldOrderIds.has(order.id) && 
        (order.status === 'Received' || order.status === 'Preparing' || order.status === 'Pending' || order.status === 'Confirmed')
      )

      if (trulyNewOrders.length > 0) {
        console.log(`${trulyNewOrders.length} neue Bestellung(en) empfangen.`)
        this.ungeseheneBestellungenCount += trulyNewOrders.length
        this.newOrderSoundTrigger++
        
        // Markiere neue Bestellungen, damit sie in der Küchen-Ansicht hervorgehoben werden können
        newOrders = newOrders.map(order => {
          // Markiere Bestellungen nur als "neu", wenn sie tatsächlich gerade erst empfangen wurden
          if (trulyNewOrders.some(newOrder => newOrder.id === order.id)) {
            return { ...order, isNew: true };
          }
          // Behalte die isNew-Eigenschaft, wenn sie bereits existiert, ansonsten setze sie auf false
          const existingOrder = this.kitchenOrders.find(o => o.id === order.id);
          return { 
            ...order, 
            isNew: existingOrder ? existingOrder.isNew : false 
          };
        });
      }

      // Speichere die aktualisierten Bestellungen im Store
      this.kitchenOrders = newOrders
      
      // Nach einer festgelegten Zeit den "neu"-Status entfernen
      if (trulyNewOrders.length > 0) {
        setTimeout(() => {
          console.log("Entferne Neu-Markierung von Bestellungen...");
          this.kitchenOrders = this.kitchenOrders.map(order => ({
            ...order, 
            isNew: false
          }));
        }, 5000); // Nach 5 Sekunden als nicht mehr neu betrachten
      }
      
      this.loading = false
      this.error = null
    },
    resetUnseenOrdersCount() {
      console.log("Resetting unseen orders count.");
      this.ungeseheneBestellungenCount = 0;
    },
    async initializeKitchenConnection() {
      console.log("OrderStore: Initialisiere Küchen-WebSocket...");
      try {
        // Importiere die Verbindungsfunktion dynamisch
        const { connectToKitchenHub } = await import('@/services/api/orderService');
        
        // Starte immer die Verbindung - diese startet automatisch Polling als Fallback
        await connectToKitchenHub();
        
        console.log("OrderStore: Küchen-WebSocket Initialisierung abgeschlossen");
        
        // Stelle sicher, dass Daten geladen werden
        setTimeout(() => {
          this.fetchKitchenOrders().catch(err => 
            console.error("OrderStore: Fehler beim initialen Laden nach WebSocket-Init:", err)
          );
        }, 1000);
        
        } catch (error) {
        console.warn("OrderStore: Küchen-WebSocket fehlgeschlagen, Polling sollte aktiv sein:", error);
        // Status wird vom orderService gesetzt
      }
    },
    disconnectKitchenConnection() {
      console.log('Trenne Küchen-WebSocket-Verbindung...')
      disconnectFromKitchenHub()
      this.setKitchenConnectionStatus('disconnected')
    },
    async fetchOrders() {
      this.loading = true
      this.error = null
      try {
        const response = await instance.get('/orders/active')
        this.orders = Array.isArray(response.data) ? response.data : []
        
        // Payment requests aus den Orders extrahieren
        const ordersWantingPay = this.orders.filter(o => o.wantsToPay && !['Completed', 'Cancelled'].includes(o.status));
        this.waiterNotifications.paymentRequests = ordersWantingPay.length;
        
        // paymentRequests Array mit echten Daten befüllen
        const existingIds = new Set(this.paymentRequests.map(pr => pr.id));
        ordersWantingPay.forEach(order => {
          if (!existingIds.has(order.id)) {
            this.paymentRequests.push({
              ...order,
              isNew: true,
              receivedAt: new Date()
            });
          }
        });
        // Entferne bezahlte/stornierte Requests
        const activePayIds = new Set(ordersWantingPay.map(o => o.id));
        this.paymentRequests = this.paymentRequests.filter(pr => activePayIds.has(pr.id));

        // Also fetch waiter calls when fetching orders
        await this.fetchWaiterCalls();

        return this.orders
      } catch (error) {
        console.error('Error fetching orders:', error)
        this.error = error.message || 'Fehler beim Laden der Bestellungen'
        this.orders = []
        return []
      } finally {
        this.loading = false
      }
    },
    
    async fetchKitchenOrders() {
      this.loading = true
      this.error = null
      
      try {
        const response = await instance.get('/orders/active')
        const ordersData = Array.isArray(response.data) ? response.data : [];
        this.setKitchenOrders(ordersData || [], false)
        return this.kitchenOrders
      } catch (error) {
        console.error('Error fetching kitchen orders:', error)
        this.error = error.message || 'Fehler beim Laden der Küchenbestellungen'
        this.setKitchenOrders([], false)
        return []
      } finally {
        this.loading = false
      }
    },
    
    async getOrderById(orderId) {
      this.loading = true
      this.error = null
      
      try {
        const response = await instance.get(`/orders/${orderId}`)
        this.currentOrder = response.data
        return this.currentOrder
      } catch (error) {
        this.error = error.message || 'Fehler beim Laden der Bestellung'
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async updateOrderStatus(orderId, status) {
      this.loading = true
      this.error = null
      
      try {
        console.log(`Versuche Bestellstatus zu aktualisieren: Order ID ${orderId}, Status ${status}`);
        
        // Direkt den orderService für die API-Anfrage verwenden
        const { orderService } = await import('../services/api/orderService');
        
        // Rufe die service Methode auf, die wir bereits korrigiert haben
        const response = await orderService.updateOrderStatus(orderId, status);
        
        console.log(`Order ${orderId} status updated to ${status}`);
        
        // Aktualisiere den Status in beiden Arrays (wenn vorhanden)
        // 1. Im regulären orders Array
        const index = this.orders.findIndex(o => o.id === orderId);
        if (index !== -1) {
            this.orders[index].status = status;
            this.orders[index]._lastUpdated = new Date().toISOString();
            this.waiterNotifications.paymentRequests = this.orders.filter(o => o.wantsToPay && !['Completed', 'Cancelled'].includes(o.status)).length;
        }
        
        // 2. Im kitchenOrders Array
        const kitchenIndex = this.kitchenOrders.findIndex(o => o.id === orderId);
        if (kitchenIndex !== -1) {
            this.kitchenOrders[kitchenIndex].status = status;
            this.kitchenOrders[kitchenIndex]._lastUpdated = new Date().toISOString();
        }
        
        // 3. Manuelles Auslösen eines Events für alle verbundenen Clients
        // Dies aktualisiert speziell die OrderStatus-Komponente
        console.log(`Dispatching order-status-update event for order ${orderId}`);
        window.dispatchEvent(new CustomEvent('order-status-update', { 
            detail: { 
                orderId: orderId, 
                status: status,
                timestamp: new Date().toISOString()
            } 
        }));
        
        // Polling übernimmt die Aktualisierung für alle verbundenen Clients

        return response;
      } catch (error) {
        console.error(`Error updating order ${orderId} status to ${status}:`, error);
        this.error = error.message || 'Fehler beim Aktualisieren der Bestellung'
        throw error
      } finally {
        this.loading = false
      }
    },
    
    async createOrder(orderData) {
      this.loading = true
      this.error = null
      
      try {
        const response = await instance.post('/orders', orderData)
        this.fetchOrders();
        return response.data
      } catch (error) {
        this.error = error.message || 'Fehler beim Erstellen der Bestellung'
        throw error
      } finally {
        this.loading = false
      }
    },

    async requestPaymentBySession(sessionId) {
      this.loading = true;
      this.error = null;
      try {
        await paymentRequestService.requestPaymentBySession(sessionId);

        const affectedOrders = this.getOrdersBySessionId(sessionId);
        affectedOrders.forEach(order => {
          const index = this.orders.findIndex(o => o.id === order.id);
          if (index !== -1 && !['Completed', 'Cancelled'].includes(this.orders[index].status)) {
            this.orders[index].wantsToPay = true;
          }
        });

        this.waiterNotifications.paymentRequests = this.orders.filter(o => o.wantsToPay && !['Completed', 'Cancelled'].includes(o.status)).length;
        
        console.log(`Payment requested for session ${sessionId}`);

      } catch (error) {
        console.error('Error requesting payment by session:', error);
        this.error = error.message || 'Failed to request payment';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async markAsPaid(orderId) {
      this.loading = true;
      this.error = null;
      try {
        await paymentRequestService.markOrderAsPaid(orderId);

        const index = this.orders.findIndex(o => o.id === orderId);
        if (index !== -1) {
          this.orders[index].status = 'Completed';
          this.orders[index].wantsToPay = false;
        }

        this.paymentRequests = this.paymentRequests.filter(pr => pr.id !== orderId);
        this.waiterNotifications.paymentRequests = this.paymentRequests.length;

        const kitchenIndex = this.kitchenOrders.findIndex(o => o.id === orderId);
        if (kitchenIndex !== -1) {
          this.kitchenOrders.splice(kitchenIndex, 1);
        }
      } catch (error) {
        console.error(`Error marking order ${orderId} as paid:`, error);
        this.error = error.message || 'Failed to mark order as paid';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async cancelOrder(orderId) {
      this.loading = true;
      this.error = null;
      try {
        await paymentRequestService.cancelOrder(orderId);

        const index = this.orders.findIndex(o => o.id === orderId);
        if (index !== -1) {
          this.orders[index].status = 'Cancelled';
          this.orders[index].wantsToPay = false;
        }

        this.paymentRequests = this.paymentRequests.filter(pr => pr.id !== orderId);
        this.waiterNotifications.paymentRequests = this.paymentRequests.length;

        const kitchenIndex = this.kitchenOrders.findIndex(o => o.id === orderId);
        if (kitchenIndex !== -1) {
          this.kitchenOrders.splice(kitchenIndex, 1);
        }
      } catch (error) {
        console.error(`Error cancelling order ${orderId}:`, error);
        this.error = error.message || 'Failed to cancel order';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    handlePaymentRequest(orders) {
      if (!orders) return;

      const orderArray = Array.isArray(orders) ? orders : [orders];
      console.log('Neue Zahlungsanfrage erhalten:', orderArray);

      orderArray.forEach(order => {
        const index = this.orders.findIndex(o => o.id === order.id);
        if (index !== -1) {
          this.orders[index].wantsToPay = true;
        } else {
          this.fetchOrders();
        }
        
        const existingIndex = this.paymentRequests.findIndex(pr => pr.id === order.id);
        if (existingIndex === -1) {
          this.paymentRequests.push({
            ...order,
            isNew: true,
            receivedAt: new Date()
          });
        }
      });

      this.newOrderSoundTrigger++;
      
      this.waiterNotifications.paymentRequests = this.paymentRequests.length;
      this.waiterNotifications.hasNewPaymentRequest = true;

      setTimeout(() => {
        this.fetchOrders();
      }, 300);

      return orderArray;
    },

    updatePaymentRequests(orders) {
      if (!orders) return;

      const orderArray = Array.isArray(orders) ? orders : [orders];
      
      const now = new Date();
      
      this.paymentRequests = orderArray.map(order => {
        const existingRequest = this.paymentRequests.find(pr => pr.id === order.id);
        
        return {
          ...order,
          isNew: existingRequest ? 
                 (existingRequest.isNew && 
                  (now - new Date(existingRequest.receivedAt)) < 5000) : 
                 false,
          receivedAt: existingRequest ? existingRequest.receivedAt : now
        };
      });
      
      this.waiterNotifications.paymentRequests = this.ordersWantingToPay.length;
    },

    setWaiterNotificationCount(count) {
      this.waiterNotifications.paymentRequests = count;
    },

    async markPaymentRequestsAsSeen() {
      try {
        await paymentRequestService.markPaymentRequestsAsSeen();
        this.waiterNotifications.hasNewPaymentRequest = false;
        
        this.paymentRequests = this.paymentRequests.map(pr => ({
          ...pr,
          isNew: false
        }));
      } catch (err) {
        console.error("Fehler beim Markieren von Zahlungsanfragen als gesehen:", err);
      }
    },

    clearWaiterNotifications() {
      console.log('Clearing waiter notifications.');
      this.waiterNotifications.paymentRequests = 0;
      this.waiterNotifications.hasNewPaymentRequest = false;
      this.waiterNotifications.waiterCalls = 0;
      this.waiterNotifications.hasNewWaiterCall = false;
      
      this.markPaymentRequestsAsSeen();
    },

    handleWaiterCall(sessionData) {
      if (!sessionData) return;

      const sessionArray = Array.isArray(sessionData) ? sessionData : [sessionData];
      console.log('Neuer Kellner-Ruf erhalten:', sessionArray);

      sessionArray.forEach(session => {
        const existingIndex = this.waiterCalls.findIndex(wc => wc.sessionId === session.sessionId);
        if (existingIndex === -1) {
          this.waiterCalls.push({
            ...session,
            isNew: true,
            receivedAt: new Date()
          });
        } else {
          // Update existing waiter call
          this.waiterCalls[existingIndex] = {
            ...this.waiterCalls[existingIndex],
            ...session,
            isNew: session.needsService && !this.waiterCalls[existingIndex].isNew ? true : this.waiterCalls[existingIndex].isNew
          };
        }
      });

      // Remove acknowledged calls (needsService === false)
      this.waiterCalls = this.waiterCalls.filter(wc => wc.needsService !== false);

      this.newOrderSoundTrigger++;
      this.waiterNotifications.waiterCalls = this.waiterCalls.length;
      this.waiterNotifications.hasNewWaiterCall = this.waiterCalls.some(wc => wc.isNew);

      return sessionArray;
    },

    async acknowledgeWaiterCall(sessionId) {
      this.loading = true;
      this.error = null;
      try {
        await waiterCallService.acknowledgeWaiterCall(sessionId);

        // Remove from waiter calls list
        this.waiterCalls = this.waiterCalls.filter(wc => wc.sessionId !== sessionId);
        this.waiterNotifications.waiterCalls = this.waiterCalls.length;
        this.waiterNotifications.hasNewWaiterCall = this.waiterCalls.some(wc => wc.isNew);

        console.log(`Waiter call acknowledged for session ${sessionId}`);
      } catch (error) {
        console.error(`Error acknowledging waiter call for session ${sessionId}:`, error);
        this.error = error.message || 'Failed to acknowledge waiter call';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchWaiterCalls() {
      try {
        // Fetch all active orders to check for sessions that need service
        const response = await instance.get('/orders/active');
        const orders = Array.isArray(response.data) ? response.data : [];
        
        // Get unique sessions that need service
        const sessionsNeedingService = new Map();
        const existingCallSessionIds = new Set(this.waiterCalls.map(wc => wc.sessionId));
        
        orders.forEach(order => {
          if (order.orderSession && order.orderSession.needsService) {
            const sid = order.orderSession.uniqueIdentifier || order.orderSession.sessionUniqueId || order.orderSession.id;
            if (!sessionsNeedingService.has(sid)) {
              const isNew = !existingCallSessionIds.has(sid);
              sessionsNeedingService.set(sid, {
                sessionId: sid,
                tableId: order.tableId || order.orderSession.tableId,
                tableName: order.tableName || `Tisch ${order.tableId || order.orderSession.tableId}`,
                needsService: true,
                type: order.orderSession.wantsToPay ? 'payment' : 'service',
                createdAt: order.orderSession.createdAt || order.createdAt,
                isNew: isNew
              });
            }
          }
        });

        // Update waiter calls, preserving isNew status for existing calls
        const updatedWaiterCalls = Array.from(sessionsNeedingService.values()).map(newCall => {
          const existingCall = this.waiterCalls.find(wc => wc.sessionId === newCall.sessionId);
          if (existingCall && existingCall.isNew) {
            return { ...newCall, isNew: true };
          }
          return newCall;
        });

        this.waiterCalls = updatedWaiterCalls;
        this.waiterNotifications.waiterCalls = this.waiterCalls.length;
        this.waiterNotifications.hasNewWaiterCall = this.waiterCalls.some(wc => wc.isNew);
      } catch (error) {
        console.error('Error fetching waiter calls:', error);
      }
    }
  }
}) 